import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, matchPath } from "react-router-dom";
import { ApiContext } from "../../context/MyContext";
import { get as commsGet } from "../../service/comms/commsApi";
import { listRooms, ROOMS_PAGE_LIMIT } from "../../service/comms/roomsService";
import { getMessages } from "../../service/comms/messagesService";
import { listTickets } from "../../service/comms/supportService";
import {
  getDisplayId,
  getRoomId,
  getTicketClientName,
} from "../../pages/comms/Support/ticketUtils";
import { mergeTicketLists } from "./supportTicketsSync";
import { useRoomChannel } from "./useRoomChannel";
import { RealtimeContext } from "./RealtimeProvider";
import { useOversightAccess } from "./OversightAccessProvider";
import { useUnreadRoomSet } from "./useUnreadRoomSet";
import { playNotificationSound } from "../../common/comms/notificationSound";
import { extractMessage } from "../../utils/commsMessageMerge";
import { isOwnSender } from "../../utils/commsParticipantId";
import {
  hasMentionForViewer,
  mentionsViewer,
} from "../../utils/commsMentions";
import {
  appendMessage as cacheAppendMessage,
  markAllStale as cacheMarkAllStale,
} from "./roomMessagesCache";

/**
 * Always-on, global "unread messages across every room" tracker.
 *
 * Mounted once at the app root (above the Router), it opens a live
 * subscription to EVERY room in the caller's inbox — not just the one room
 * currently open in a Thread — so a "Messages" badge can update instantly
 * from any page. There is no "new message" event on the personal broadcast
 * channel (only named notification types like MENTION/FLAG/SLA_BREACH), so
 * per-room subscriptions are the only way to know about a plain new message
 * in a room the viewer isn't currently looking at.
 *
 * Two independent room sets are tracked (see useUnreadRoomSet for the
 * shared machinery):
 *
 *  - Chat: COACHING rooms from `/api/v1/rooms` — `unreadByRoom` /
 *    `totalUnread`, feeding the sidebar "Messages" badge.
 *  - Support: the union of tickets ASSIGNED TO THE VIEWER (`mine=true`) and
 *    the UNASSIGNED pick-up pool (`unassigned=true`) — `supportUnreadByRoom`
 *    / `supportTotalUnread`, feeding the "Support" / "My Tickets" badges.
 *    Tickets assigned to OTHER agents are silent by construction: they are
 *    never fetched, so no watcher mounts for them and they drop out of the
 *    unread map on the next resync. Counts are seeded from the ticket's own
 *    `unread_count` where the backend provides it, and otherwise from
 *    `/api/v1/rooms?room_type=SUPPORT` or realtime increments alone.
 *
 * The support resync also diffs the fetched ticket union to detect NEW
 * tickets (every agent sees the unassigned pool arrive), firing the same
 * sound/notify pipeline with `isNewTicket: true`. That diff is now a backstop:
 * CommsNotifyProvider triggers an instant resync on the realtime
 * TICKET_CREATED / TICKET_ASSIGNED / SLA_BREACH broadcasts, so the poll only
 * has to cover a dropped socket.
 *
 * The raw `message` event payload shape isn't decoded anywhere else in this
 * codebase (useRoomMessages.js's own onMessage handler ignores it and just
 * refetches), so the per-message increment here is deliberately best-effort;
 * the periodic resync is the actual source of truth.
 */
const RoomsUnreadContext = createContext({
  unreadByRoom: new Map(),
  totalUnread: 0,
  mentionRoomIds: new Set(),
  supportUnreadByRoom: new Map(),
  supportTotalUnread: 0,
  supportTicketsVersion: 0,
  clientRoomIndex: new Map(),
});

export const useRoomsUnread = () => useContext(RoomsUnreadContext);

const RESYNC_INTERVAL_MS = 45000;
// The ticket lifecycle now arrives in realtime: TICKET_CREATED, TICKET_ASSIGNED
// and SLA_BREACH all land on the agent's personal broadcast channel and call
// `requestSupportResync()` immediately (see CommsNotifyProvider). This poll is
// the safety net behind that — for a dropped socket or an event type nobody
// publishes yet — not the primary path, so it no longer needs to run every 10s.
const SUPPORT_RESYNC_INTERVAL_MS = 45000;
// Mention backfill: listRooms carries no "unread mentions" field, so a mention
// that arrived before this session started is invisible until we look at the
// messages themselves. Bounded hard — only rooms that actually have unread,
// only the unread tail of each, and only the busiest handful.
const MENTION_SCAN_MAX_ROOMS = 15;
const MENTION_SCAN_MAX_MESSAGES = 50;
const BATCH_SIZE = 8;
const BATCH_INTERVAL_MS = 250;

/**
 * Build the `client_id -> room` index from a COACHING rooms listing, reusing
 * `prev` when nothing changed.
 *
 * The returned reference MUST stay stable across unchanged resyncs: this
 * provider sits above the Router, so a fresh Map every RESYNC_INTERVAL_MS
 * would re-render the entire app tree on a timer. Same guard as
 * useUnreadRoomSet's setRoomIds.
 *
 * @param {Map<string, Object>} prev
 * @param {Array<Object>} rooms - Raw `/api/v1/rooms` entries.
 * @returns {Map<string, {roomId: string, displayId: any, clientName: string,
 *   lastMessage: string|null, lastMessageAt: string|null, createdAt: string|null,
 *   lastSeq: number}>}
 */
function buildClientRoomIndex(prev, rooms) {
  const next = new Map();
  for (const r of rooms) {
    // A client with no id can't be looked up, and a room with no id can't be
    // opened — skip either rather than storing an unusable entry.
    if (r?.client_id == null || r?.id == null) {
      continue;
    }
    next.set(String(r.client_id), {
      roomId: String(r.id),
      displayId: r.display_id,
      clientName: r.client_name,
      lastMessage: r.last_message || null,
      lastMessageAt: r.last_activity_at || null,
      // Fixed for a room's lifetime, so the change detector below needn't
      // compare it; it orders rooms that have no messages yet.
      createdAt: r.created_at || null,
      lastSeq: Number(r.last_seq) || 0,
    });
  }
  // lastSeq is the change detector for message content: it advances on every
  // new message, so comparing it catches a changed preview without deep
  // equality over the whole entry. It also covers lastMessageAt, which can only
  // move when a new message arrives — so that field needs no comparison of its
  // own, and adding one would cost a whole-tree re-render for nothing.
  if (prev.size === next.size) {
    let identical = true;
    for (const [clientId, entry] of next) {
      const before = prev.get(clientId);
      if (
        !before ||
        before.roomId !== entry.roomId ||
        before.lastSeq !== entry.lastSeq
      ) {
        identical = false;
        break;
      }
    }
    if (identical) return prev;
  }
  return next;
}

/**
 * Listeners fired for every inbound message in a non-active room (after the
 * unread increment) and for newly detected support tickets. Module-scoped so
 * notify layers (toasts, OS notifications) can subscribe without threading
 * React context through. Callback shape:
 *   ({ roomId, msg, roomSummary }) => void
 * where support payloads carry `roomSummary.kind === "support"` and
 * new-ticket alerts additionally `roomSummary.isNewTicket === true` with a
 * null `msg`.
 */
const incomingMessageListeners = new Set();

/**
 * Subscribe to "new message arrived in a room the viewer isn't looking at"
 * (and new-ticket alerts — see above).
 * @param {Function} cb
 * @returns {Function} Unsubscribe.
 */
export const subscribeIncomingMessages = (cb) => {
  incomingMessageListeners.add(cb);
  return () => incomingMessageListeners.delete(cb);
};

/** Listeners the mounted provider registers to serve requestSupportResync. */
const supportResyncListeners = new Set();

/**
 * New-ticket detection state, module-scoped (survives provider remounts) and
 * keyed by viewer identity so a login switch never inherits the previous
 * user's seen set. `seen` = every ticket id ever observed this session;
 * the first successful fetch per identity seeds it silently.
 */
const newTicketTracker = { key: null, seen: new Set(), seeded: false };

/**
 * Mark a ticket as already-seen so the next resync doesn't alert on it —
 * call right after creating a ticket yourself.
 * @param {string} roomId
 */
export const markTicketSeen = (roomId) => {
  if (roomId != null) {
    newTicketTracker.seen.add(String(roomId));
  }
};

/**
 * Ask the provider to refresh the support ticket set right now — call after
 * a ticket action (resolve / pick-up / escalate) so badges update without
 * waiting for the 45s resync tick.
 *
 * @param {{ roomId?: string, eventType?: string }} [detail] - Which ticket
 *   changed, when that is known. A listener holding a ticket list can then
 *   re-read that one ticket instead of its whole page; omitting it still means
 *   "something changed, reload", which is what every action call site does.
 */
export const requestSupportResync = (detail) => {
  for (const listener of supportResyncListeners) {
    try {
      listener(detail);
    } catch (err) {
      console.error("Support resync listener failed:", err);
    }
  }
};

/**
 * Listen for resync requests without being the provider.
 *
 * The provider registers its own listener to refetch badge state; this lets a
 * screen subscribe to the same signal and react more cheaply — see
 * `useSupportQueue`, which splices the named ticket rather than re-pulling the
 * queue.
 *
 * @param {(detail?: { roomId?: string, eventType?: string }) => void} listener
 * @returns {() => void} Unsubscribe.
 */
export const subscribeSupportResync = (listener) => {
  supportResyncListeners.add(listener);
  return () => supportResyncListeners.delete(listener);
};

/** Listeners the mounted provider registers to serve markRoomMentioned. */
const mentionListeners = new Set();

/**
 * Flag a room as holding an unread message that @-mentions the viewer — the
 * inbox renders it with a mention badge until the room is read. Callable from
 * outside React (CommsNotifyProvider's MENTION broadcast branch) in the same
 * style as `markTicketSeen`. No-op when the provider isn't mounted.
 * @param {string} roomId
 */
export const markRoomMentioned = (roomId) => {
  if (roomId == null) return;
  for (const listener of mentionListeners) {
    try {
      listener(String(roomId));
    } catch (err) {
      console.error("Mention listener failed:", err);
    }
  }
};

/** Listeners the mounted provider registers to serve requestChatResync. */
const chatResyncListeners = new Set();

/**
 * Ask the provider to refresh the COACHING room set right now.
 *
 * The chat twin of `requestSupportResync`. Chat rooms otherwise refresh only on
 * the 45s tick, which is fine for a conversation the coach is watching — the
 * room's own channel carries those — but not for a write that changes MANY
 * rooms at once. After a bulk send, a dozen `last_message` previews in the dock
 * roster are stale and nothing else will correct them until the tick.
 */
export const requestChatResync = () => {
  for (const listener of chatResyncListeners) {
    try {
      listener();
    } catch (err) {
      console.error("Chat resync listener failed:", err);
    }
  }
};

/** Non-visual — just keeps one room's live channel open and reports events up. */
function RoomUnreadWatcher({ roomId, viewerUserId, isActive, onMessage, onRead }) {
  const handlers = useMemo(
    () => ({
      onMessage: (data) => {
        // Feed the global message cache with EVERY message — including own
        // and active-room ones — so returning to a thread renders instantly.
        // Seq-merge makes double appends (Thread also appends) idempotent.
        const msg = extractMessage(data);
        if (msg) {
          cacheAppendMessage(roomId, msg);
        }
        if (isActive) {
          // The open Thread's own read-receipt flow owns this room's count.
          return;
        }
        // Own sends must never chime. `sender_id` alone is not enough —
        // ms-agents may put either id form there (see commsParticipantId.js),
        // so match against both. The payload is flat or nested; `msg` is
        // already normalized, `data` covers the thin-payload fallback.
        const candidate = msg ?? data?.message ?? data;
        if (isOwnSender(candidate, viewerUserId)) {
          return;
        }
        onMessage(roomId, msg);
      },
      onRead: (data) => {
        if (data?.user_id != null && String(data.user_id) === String(viewerUserId)) {
          onRead(roomId);
        }
      },
    }),
    [roomId, viewerUserId, isActive, onMessage, onRead]
  );

  useRoomChannel(roomId, handlers);
  return null;
}

export function RoomsUnreadProvider({ children }) {
  const { empId } = useContext(ApiContext);
  const viewerUserId = empId;
  const location = useLocation();
  const { connectionEpoch } = useContext(RealtimeContext);
  // The support watch set no longer depends on the staff tier (everyone
  // watches mine ∪ unassigned), but other consumers key off the probe —
  // keep it warm.
  const { ensureProbe } = useOversightAccess();

  useEffect(() => {
    ensureProbe();
  }, [ensureProbe]);

  const activeChatRoomId =
    matchPath("/messages/:roomId", location.pathname)?.params?.roomId ?? null;
  const activeTicketRoomId =
    matchPath("/support/tickets/:id", location.pathname)?.params?.id ?? null;

  // client_id -> { roomId, displayId, clientName } for every COACHING room in
  // the viewer's inbox, populated as a side effect of fetchChatRooms below.
  const [clientRoomIndex, setClientRoomIndex] = useState(() => new Map());

  // Shared "something arrived" pipeline: chime once, then fan out to the
  // notify layer (toasts / OS notifications).
  const fireIncoming = useCallback((payload) => {
    playNotificationSound();
    for (const listener of incomingMessageListeners) {
      try {
        listener(payload);
      } catch (err) {
        console.error("Incoming-message listener failed:", err);
      }
    }
  }, []);

  const fetchChatRooms = useCallback(async () => {
    const res = await listRooms(commsGet, { room_type: "COACHING", limit: ROOMS_PAGE_LIMIT });
    const rooms = Array.isArray(res?.rooms) ? res.rooms : [];
    // Piggyback the client -> room index on the fetch this provider already
    // makes every RESYNC_INTERVAL_MS, so screens outside Messages can resolve
    // "does this user have a coaching room?" without a request of their own.
    setClientRoomIndex((prev) => buildClientRoomIndex(prev, rooms));
    return rooms.map((r) => ({
      id: r.id,
      unreadCount: Number(r.unread_count) || 0,
      summary: {
        client_name: r.client_name,
        display_id: r.display_id,
        client_id: r.client_id,
      },
    }));
  }, []);

  // Tickets flagged "new" and not yet dealt with — they count as 1 unread in
  // the support badge until opened, picked up by someone else, or gone from
  // the queue. A brand-new ticket is typically unassigned, so it would
  // otherwise never reach the assigned-to-me count set.
  const [newTicketIds, setNewTicketIds] = useState(() => new Set());
  // The unread_count join is best-effort (the SUPPORT room_type filter is
  // not a confirmed backend feature) — warn once, not every resync.
  const supportJoinWarnedRef = useRef(false);
  // Same once-only treatment when one of the two ticket list calls degrades.
  const ticketFetchWarnedRef = useRef(false);

  const fetchSupportRooms = useCallback(async () => {
    const [mineRes, unassignedRes, roomsRes] = await Promise.allSettled([
      listTickets(commsGet, { status: "open", mine: true }),
      listTickets(commsGet, { unassigned: true }),
      listRooms(commsGet, { room_type: "SUPPORT" }),
    ]);
    // Throws only when BOTH ticket calls failed (useUnreadRoomSet keeps its
    // last-known state); a single failure just shrinks the union.
    const { tickets, mineDegraded, unassignedDegraded } = mergeTicketLists(
      mineRes,
      unassignedRes
    );
    if ((mineDegraded || unassignedDegraded) && !ticketFetchWarnedRef.current) {
      ticketFetchWarnedRef.current = true;
      console.error(
        unassignedDegraded
          ? "RoomsUnreadProvider: unassigned-ticket fetch unavailable; new-ticket alerts degraded to assigned tickets only"
          : "RoomsUnreadProvider: mine-ticket fetch unavailable; watching the unassigned pool only",
        unassignedDegraded ? unassignedRes.reason : mineRes.reason
      );
    }

    // Optional unread_count seed: join ticket rooms against the generic
    // rooms listing. On failure, `null` counts tell useUnreadRoomSet to
    // preserve local state (realtime increments only).
    let unreadJoin = null;
    if (roomsRes.status === "fulfilled") {
      const rooms = Array.isArray(roomsRes.value?.rooms) ? roomsRes.value.rooms : [];
      unreadJoin = new Map(rooms.map((r) => [r.id, Number(r.unread_count) || 0]));
    } else if (!supportJoinWarnedRef.current) {
      supportJoinWarnedRef.current = true;
      console.error(
        "RoomsUnreadProvider: SUPPORT rooms unread join unavailable; support badges run on realtime increments only",
        roomsRes.reason
      );
    }

    const toSummary = (t) => ({
      kind: "support",
      display_id: getDisplayId(t),
      subject: t?.subject,
      client_name: getTicketClientName(t),
      client_id: t?.client_id,
    });

    // Diff the fetched union (mine ∪ unassigned) to detect tickets that
    // appeared since the last resync — every agent alerts on new arrivals in
    // the pick-up pool. First successful fetch per identity seeds silently —
    // no alert storm on boot/login.
    const identityKey = String(empId ?? "");
    if (newTicketTracker.key !== identityKey) {
      newTicketTracker.key = identityKey;
      newTicketTracker.seen = new Set();
      newTicketTracker.seeded = false;
    }
    const freshIds = new Set();
    for (const t of tickets) {
      const roomId = getRoomId(t);
      if (!roomId || newTicketTracker.seen.has(String(roomId))) {
        continue;
      }
      newTicketTracker.seen.add(String(roomId));
      if (newTicketTracker.seeded) {
        freshIds.add(String(roomId));
        fireIncoming({
          roomId,
          msg: null,
          roomSummary: { ...toSummary(t), isNewTicket: true },
        });
      }
    }
    newTicketTracker.seeded = true;

    // Refresh the new-ticket badge set: a flagged ticket keeps counting while
    // it is still in the fetched union — which is by construction exactly
    // "mine or waiting unassigned", so no client-side assignee matching is
    // needed. Opening it clears it via the navigation effect; a pick-up by
    // another agent drops it from the union (and this set) on the next resync.
    const pendingEligibleIds = new Set();
    for (const t of tickets) {
      const roomId = getRoomId(t);
      if (roomId) {
        pendingEligibleIds.add(String(roomId));
      }
    }
    setNewTicketIds((prev) => {
      const next = new Set();
      for (const id of freshIds) {
        if (pendingEligibleIds.has(id)) {
          next.add(id);
        }
      }
      for (const id of prev) {
        if (pendingEligibleIds.has(id)) {
          next.add(id);
        }
      }
      const unchanged =
        next.size === prev.size && Array.from(next).every((id) => prev.has(id));
      return unchanged ? prev : next;
    });

    // Watched (counted) set = the whole union. Both halves are server-scoped
    // (mine=true / unassigned=true), so client-side identity matching — which
    // silently broke when empId didn't match ms-agents assignee ids — is no
    // longer load-bearing.
    return tickets
      .map((t) => {
        const id = getRoomId(t);
        return id
          ? {
              id,
              unreadCount: unreadJoin ? unreadJoin.get(id) ?? null : null,
              summary: toSummary(t),
            }
          : null;
      })
      .filter(Boolean);
  }, [empId, fireIncoming]);

  const chatSet = useUnreadRoomSet({
    fetchRooms: fetchChatRooms,
    activeRoomId: activeChatRoomId,
    connectionEpoch,
    onIncoming: fireIncoming,
    resyncIntervalMs: RESYNC_INTERVAL_MS,
    batchSize: BATCH_SIZE,
    batchIntervalMs: BATCH_INTERVAL_MS,
  });

  const supportSet = useUnreadRoomSet({
    fetchRooms: fetchSupportRooms,
    activeRoomId: activeTicketRoomId,
    connectionEpoch,
    onIncoming: fireIncoming,
    resyncIntervalMs: SUPPORT_RESYNC_INTERVAL_MS,
    batchSize: BATCH_SIZE,
    batchIntervalMs: BATCH_INTERVAL_MS,
  });

  // --- Unread @-mentions (chat rooms only) ---------------------------------
  // Fed from three places: the live message watcher, the MENTION broadcast,
  // and — for mentions that predate this session — the backfill scan below.
  const [mentionRoomIds, setMentionRoomIds] = useState(() => new Set());

  const addMentionRoom = useCallback((roomId) => {
    setMentionRoomIds((prev) => {
      const key = String(roomId);
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const clearMentionRoom = useCallback((roomId) => {
    setMentionRoomIds((prev) => {
      const key = String(roomId);
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  // Serve markRoomMentioned() calls (the MENTION broadcast can land before —
  // or instead of — the per-room watcher's message event).
  useEffect(() => {
    mentionListeners.add(addMentionRoom);
    return () => mentionListeners.delete(addMentionRoom);
  }, [addMentionRoom]);

  // Watcher pipeline: flag the room before the unread increment. Own sends
  // never reach this (RoomUnreadWatcher filters them upstream).
  const handleChatMessage = useCallback(
    (roomId, msg) => {
      if (msg && mentionsViewer(msg, viewerUserId)) {
        addMentionRoom(roomId);
      }
      chatSet.handleMessage(roomId, msg);
    },
    [chatSet.handleMessage, viewerUserId, addMentionRoom]
  );

  // Reading clears the flag along with the count: the realtime read echo…
  const handleChatReadReset = useCallback(
    (roomId) => {
      clearMentionRoom(roomId);
      chatSet.handleReadReset(roomId);
    },
    [chatSet.handleReadReset, clearMentionRoom]
  );

  // …opening the room (navigation = read, same rule as the unread count)…
  useEffect(() => {
    if (activeChatRoomId) {
      clearMentionRoom(activeChatRoomId);
    }
  }, [activeChatRoomId, clearMentionRoom]);

  // Backfill for mentions that arrived before this session started.
  //
  // Without this the whole feature is invisible on a fresh load: the realtime
  // watcher only sees messages that arrive while the tab is open, so a room
  // tagged an hour ago showed a plain unread badge and an uncolored card.
  // `unread_count` is the number of trailing unread messages, so fetching that
  // many newest messages inspects exactly the unread tail — nothing older.
  // Scanned rooms are remembered per unread count so a resync doesn't refetch.
  const mentionScanRef = useRef(new Map());
  useEffect(() => {
    const candidates = Array.from(chatSet.unreadByRoom.entries())
      .filter(([, count]) => count > 0)
      .slice(0, MENTION_SCAN_MAX_ROOMS);
    if (candidates.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      for (const [roomId, count] of candidates) {
        if (cancelled) return;
        const key = String(roomId);
        if (mentionScanRef.current.get(key) === count) continue;
        mentionScanRef.current.set(key, count);
        try {
          const res = await getMessages(commsGet, key, {
            limit: Math.min(count, MENTION_SCAN_MAX_MESSAGES),
          });
          if (cancelled) return;
          if (hasMentionForViewer(res?.messages, viewerUserId)) {
            addMentionRoom(key);
          }
        } catch (err) {
          // Best-effort: a room we can't read simply doesn't get flagged, and
          // forgetting it lets a later resync retry.
          mentionScanRef.current.delete(key);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chatSet.unreadByRoom, viewerUserId, addMentionRoom]);

  // …and the 45s resync reporting a server-side read from another device.
  // unreadByRoom is keyed by the raw room id — normalize before comparing.
  useEffect(() => {
    setMentionRoomIds((prev) => {
      if (prev.size === 0) return prev;
      const stillUnread = new Set();
      for (const [roomId, count] of chatSet.unreadByRoom) {
        if (count > 0) stillUnread.add(String(roomId));
      }
      const next = new Set();
      for (const id of prev) {
        if (stillUnread.has(id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [chatSet.unreadByRoom]);

  // On every websocket (re)connect, events may have been missed while down:
  // cached message buffers can have silent gaps. Mark the cache stale once at
  // the provider level (each set already resyncs its own counts on epoch).
  const epochRef = useRef(connectionEpoch);
  useEffect(() => {
    if (connectionEpoch === epochRef.current) {
      return;
    }
    epochRef.current = connectionEpoch;
    cacheMarkAllStale();
  }, [connectionEpoch]);

  // Opening a ticket's thread clears its "new" flag (its unread count is
  // cleared by the set's own navigation effect).
  useEffect(() => {
    if (!activeTicketRoomId) {
      return;
    }
    setNewTicketIds((prev) => {
      const key = String(activeTicketRoomId);
      if (!prev.has(key)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, [activeTicketRoomId]);

  // Serve requestSupportResync() calls (ticket actions want instant badges).
  const supportResyncRef = useRef(supportSet.resync);
  supportResyncRef.current = supportSet.resync;
  useEffect(() => {
    const listener = () => supportResyncRef.current();
    supportResyncListeners.add(listener);
    return () => supportResyncListeners.delete(listener);
  }, []);

  // Serve requestChatResync() calls (a bulk send changes many rooms' previews).
  const chatResyncRef = useRef(chatSet.resync);
  chatResyncRef.current = chatSet.resync;
  useEffect(() => {
    const listener = () => chatResyncRef.current();
    chatResyncListeners.add(listener);
    return () => chatResyncListeners.delete(listener);
  }, []);

  // roomChannelRegistry shares one real subscription per room id, so a room
  // present in BOTH sets would double-count and double-chime. The sets are
  // disjoint by construction (COACHING vs SUPPORT rooms) — this filter is
  // cheap insurance in case an id ever appears in both.
  const chatIdSet = useMemo(
    () => new Set(chatSet.visibleRoomIds),
    [chatSet.visibleRoomIds]
  );
  const supportWatcherIds = useMemo(
    () => supportSet.visibleRoomIds.filter((id) => !chatIdSet.has(id)),
    [supportSet.visibleRoomIds, chatIdSet]
  );

  // A new-not-yet-opened ticket counts as at least 1 unread so it shows up
  // in the sidebar badge and queue pills even before any message arrives.
  const supportUnreadByRoom = useMemo(() => {
    if (newTicketIds.size === 0) {
      return supportSet.unreadByRoom;
    }
    const merged = new Map(supportSet.unreadByRoom);
    for (const id of newTicketIds) {
      merged.set(id, Math.max(merged.get(id) || 0, 1));
    }
    return merged;
  }, [supportSet.unreadByRoom, newTicketIds]);

  const supportTotalUnread = useMemo(
    () =>
      Array.from(supportUnreadByRoom.values()).reduce((sum, count) => sum + count, 0),
    [supportUnreadByRoom]
  );

  const value = useMemo(
    () => ({
      unreadByRoom: chatSet.unreadByRoom,
      totalUnread: chatSet.total,
      mentionRoomIds,
      supportUnreadByRoom,
      supportTotalUnread,
      supportTicketsVersion: supportSet.resyncVersion,
      clientRoomIndex,
    }),
    [
      chatSet.unreadByRoom,
      chatSet.total,
      mentionRoomIds,
      supportUnreadByRoom,
      supportTotalUnread,
      supportSet.resyncVersion,
      clientRoomIndex,
    ]
  );

  return (
    <RoomsUnreadContext.Provider value={value}>
      {children}
      {chatSet.visibleRoomIds.map((roomId) => (
        <RoomUnreadWatcher
          key={roomId}
          roomId={roomId}
          viewerUserId={viewerUserId}
          isActive={String(roomId) === String(activeChatRoomId)}
          onMessage={handleChatMessage}
          onRead={handleChatReadReset}
        />
      ))}
      {supportWatcherIds.map((roomId) => (
        <RoomUnreadWatcher
          key={roomId}
          roomId={roomId}
          viewerUserId={viewerUserId}
          isActive={String(roomId) === String(activeTicketRoomId)}
          onMessage={supportSet.handleMessage}
          onRead={supportSet.handleReadReset}
        />
      ))}
    </RoomsUnreadContext.Provider>
  );
}
